import React, { useEffect, useState } from "react";
import { IPage } from "../../../../api/entities/page";
import { IShardRepositoryRequestParams, IShardRepository, IShardRepositoryFilterParams, ShardRepositorySortField } from "../../../../api/entities/shard-repository";
import { CONFIG } from "../../../../config/config";
import { getDefaultRequestParams, getEmptyPage } from "../../../../redux/reducers/entity";
import { useApi } from "../../../../utils/hooks/use-api";
import { storageTypeToNameMap, storageUtils } from "../../../../utils/storage-utils";
import Button from "../../../shared-ui/button";
import DetailsPanel from "../../../shared-ui/details-panel/details-panel";
import { FormPanel } from "../../../shared-ui/form/form-panel/form-panel";
import Table, { IColumnDefinition, TableSelected } from "../../../shared-ui/table/table";
import { defaultFilters, IShardRepositoryFilters, StorageNodeListFilter, StorageNodeListFilters } from "../../filters/storage-node-list-filters/storage-node-list-filters";
import styles from "./select-shard-repositories-panel.module.scss";
import { shardRepositoryService } from "../../../../api/services/shard-repository-service";

export interface IShardRepositoryPanelProps {
    open: boolean;
    onClose: () => void;
    onOpenShardRepositoryPanel: () => void;
    lastAddedShardRepository: IShardRepository;
    selection: TableSelected<IShardRepository>;
    setSelection: (selection: TableSelected<IShardRepository>) => void;
}

export const SelectShardRepositoriesPanel = (props: IShardRepositoryPanelProps) => {
    const { open } = props;

    const [storageNodePage, setStorageNodePage] = useState<IPage<IShardRepository>>(getEmptyPage());
    const [api, loading] = useApi();

    const [params, setParams] = useState<IShardRepositoryRequestParams>({ ...getDefaultRequestParams(), deleted: false });
    const [filterSearchParams, setFilterSearchParams] = useState<IShardRepositoryFilters>(defaultFilters);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const isSufficientStorageNodes = () =>  Object.keys(props.selection).length >= CONFIG.minStoragesCount;

    const columns: IColumnDefinition<IShardRepository, ShardRepositorySortField>[] = [
        {
            sortKey: ShardRepositorySortField.Type,
            label: "Type",
            className: styles["col--storage-node-type"],
            formatter: row => storageTypeToNameMap[storageUtils.getStorageProviderTypeGeneralizedType(row.storageType)]
        } as IColumnDefinition<IShardRepository, ShardRepositorySortField>,
        {
            sortKey: ShardRepositorySortField.Bucket,
            label: "Bucket / Container",
            className: styles["col--storage-node-bucket"],
            formatter: row => row.bucket
        } as IColumnDefinition<IShardRepository, ShardRepositorySortField>,
        {
            sortKey: ShardRepositorySortField.Region,
            label: "Region",
            formatter: row => row.region
        } as IColumnDefinition<IShardRepository, ShardRepositorySortField>,
    ];

    function updateFilters(filters: Partial<IShardRepositoryFilters>) {
        const mapToParams = (x: Partial<IShardRepositoryFilters>): IShardRepositoryFilterParams => {
            return {
                q: x.q,
                storageType: x.storageTypeFilter?.value
            };
        };

        let updatedFilterParams = { ...filterSearchParams, ...filters };

        updateSearchParams({ ...mapToParams(updatedFilterParams), offset: 0 });
        setFilterSearchParams(updatedFilterParams);
    }

    function updateSearchParams(sp: IShardRepositoryRequestParams) {
        setParams(searchParams => ({
            ...searchParams,
            ...sp,
        }));
    }

    function fetchData(params: IShardRepositoryRequestParams) {
        api(shardRepositoryService.getPage(params))
            .then(setStorageNodePage);
    }

    useEffect(() => {
        fetchData(params);
        // eslint-disable-next-line
    }, [params]);

    useEffect(() => {
        if (!!props.lastAddedShardRepository) {
            fetchData(params);

            props.setSelection({
                ...props.selection,
                [props.lastAddedShardRepository.id]: props.lastAddedShardRepository
            });
        }
        // eslint-disable-next-line
    }, [props.lastAddedShardRepository]);

    useEffect(() => {
        if (!isSufficientStorageNodes()) {
            setErrorMessage(`Myota requires at least ${CONFIG.minStoragesCount} shard repositories.`);
        }
        else {
            setErrorMessage('');
        }
        // eslint-disable-next-line
    }, [props.selection]);

    return (
        <>
            <DetailsPanel
                isLoading={false}
                open={open}
                onCloseClick={props.onClose}
                title="Select Shard Repositories"
                className={styles.panel}
            >
                {open && (
                    <FormPanel
                        okButtonText="Select"
                        okButtonClick={() => props.onClose()}
                        okButtonDisabled={!isSufficientStorageNodes()}
                        errorMessage={errorMessage}
                    >
                        <Button
                            testId="add-button"
                            colors="primary"
                            onClick={props.onOpenShardRepositoryPanel}
                        >
                            Add Shard Repository
                        </Button>

                        <div className={styles.border} data-testid="select-storage-nodes-list">
                            <StorageNodeListFilters
                                filterSearchParams={filterSearchParams}
                                visibleFilters={[StorageNodeListFilter.Search, StorageNodeListFilter.StorageType]}
                                onFiltersChange={updateFilters}
                            />
                            <Table
                                autoHidePaging={false}
                                className={styles.table}
                                compact={true}
                                columns={columns}
                                rows={storageNodePage.data}
                                loading={loading}
                                totalRows={storageNodePage.count}
                                testId="shard-repository-table"
                                requestParams={params}
                                onRequestParamsChanged={updateSearchParams}
                                hideCheckbox={false}
                                selected={props.selection}
                                onSetSelected={props.setSelection}
                            />
                        </div>
                    </FormPanel>
                )}
            </DetailsPanel>
        </>
    );
}
